/**
 * Permission bitmask — single source of truth shared by server and client.
 *
 * Each bit represents one capability. The wire format is `string` because
 * BigInt doesn't survive `JSON.stringify`; both ends parse with BigInt()
 * and operate with the bitwise operators below.
 *
 * Rules (enforced server-side, mirrored client-side for hint UI):
 *   - The guild owner implicitly has every bit (ALL_PERMISSIONS).
 *   - ADMINISTRATOR alone short-circuits every permission check.
 *   - A member's permissions = OR of every role they hold, OR'd with the
 *     @everyone role as the baseline ({@link computeMemberPermissions}).
 *   - Channel overrides (allow/deny masks on a per-channel basis) layer
 *     on top of the guild-level computed value when checking a specific
 *     channel — @everyone's row first, then the member's roles
 *     ({@link applyChannelOverrides}).
 *
 * Before this module existed each surface defined its own subset:
 *   - client/src/components/ContentPane.tsx
 *   - client/src/components/ServerSettingsModal.tsx
 *   - client/src/components/ChannelDrawer.tsx
 *   - server/src/auth/perms.ts
 * which is exactly the "one forgotten bit flag away from drift" risk the
 * Codex code review flagged. Now both sides import here.
 */
export const PERMISSIONS = Object.freeze({
  ADMINISTRATOR: 1n << 0n,
  MANAGE_GUILD: 1n << 1n,
  MANAGE_ROLES: 1n << 2n,
  MANAGE_CHANNELS: 1n << 3n,
  KICK_MEMBERS: 1n << 4n,
  CREATE_INVITES: 1n << 5n,
  MANAGE_MESSAGES: 1n << 6n,
  PIN_MESSAGES: 1n << 7n,
  VIEW_CHANNEL: 1n << 8n,
  SEND_MESSAGES: 1n << 9n,
  CONNECT_VOICE: 1n << 10n,
  KICK_VOICE: 1n << 11n,
  MUTE_MEMBERS: 1n << 12n,
  DEAFEN_MEMBERS: 1n << 13n,
  MOVE_MEMBERS: 1n << 14n,
  BAN_MEMBERS: 1n << 15n,
  TIMEOUT_MEMBERS: 1n << 16n,
  VIEW_AUDIT_LOG: 1n << 17n,
  MANAGE_EMOJIS: 1n << 18n,
  ATTACH_FILES: 1n << 19n,
  ADD_REACTIONS: 1n << 20n,
  EMBED_LINKS: 1n << 21n,
  READ_MESSAGE_HISTORY: 1n << 22n,
  SPEAK: 1n << 23n,
  STREAM_VIDEO: 1n << 24n,
  /**
   * May put a tournament room's audio on air.
   *
   * Deliberately outside DEFAULT_MEMBER_PERMISSIONS and never implied by any
   * other bit: this is the one permission whose use is heard by people who are
   * not in the room and cannot see that it happened. It is given to the
   * broadcast crew and nobody else.
   */
  BROADCAST_VOICE: 1n << 25n,
});

export type PermissionName = keyof typeof PERMISSIONS;

/** Union of every flag — owner has this. */
export const ALL_PERMISSIONS: bigint = Object.values(PERMISSIONS).reduce(
  (sum, bit) => sum | bit,
  0n,
);

/** Baseline given to every member who joins, on top of any role grants. */
export const DEFAULT_MEMBER_PERMISSIONS: bigint =
  PERMISSIONS.VIEW_CHANNEL |
  PERMISSIONS.SEND_MESSAGES |
  // Members invite people by default, as in Discord; taking this from
  // @everyone is what makes a server closed.
  PERMISSIONS.CREATE_INVITES |
  PERMISSIONS.CONNECT_VOICE |
  PERMISSIONS.ATTACH_FILES |
  PERMISSIONS.ADD_REACTIONS |
  PERMISSIONS.EMBED_LINKS |
  PERMISSIONS.READ_MESSAGE_HISTORY |
  PERMISSIONS.SPEAK |
  PERMISSIONS.STREAM_VIDEO;

/**
 * `true` if the given permission bigint has the requested capability.
 * ADMINISTRATOR short-circuits every check.
 */
export function hasPermission(perms: bigint, bit: bigint): boolean {
  if ((perms & PERMISSIONS.ADMINISTRATOR) === PERMISSIONS.ADMINISTRATOR) {
    return true;
  }
  return (perms & bit) === bit;
}

/**
 * Pure: fold ONE level of allow/deny override rows into a base bitmask —
 * `deny` bits are stripped first, then `allow` bits are added, with every
 * row of the level OR-folded before applying. So inside a level (several
 * roles on one channel) an allow beats a deny.
 *
 * Accepts `number | bigint` because Prisma int columns deserialise to
 * Number; callers don't have to convert each row.
 */
export function applyOverridesToPermissions(
  base: bigint,
  overrides: ReadonlyArray<{ allow: bigint | number; deny: bigint | number }>,
): bigint {
  let allow = 0n;
  let deny = 0n;
  for (const o of overrides) {
    allow |= BigInt(o.allow);
    deny |= BigInt(o.deny);
  }
  return (base & ~deny) | allow;
}

/**
 * A member's permissions inside one channel: the @everyone override first,
 * then all of their roles' overrides as one level (Discord's order). The
 * role level is more specific, so a role's deny beats an @everyone allow —
 * folding both into one level used to let an @everyone allow silently undo
 * a «read-only» role. Owners and administrators bypass this entirely; the
 * callers short-circuit them before folding.
 */
export function applyChannelOverrides(
  base: bigint,
  overrides: ReadonlyArray<{ roleId: string; allow: bigint | number; deny: bigint | number }>,
  everyoneRoleId: string,
): bigint {
  const everyone = overrides.filter((o) => o.roleId === everyoneRoleId);
  const roles = overrides.filter((o) => o.roleId !== everyoneRoleId);
  return applyOverridesToPermissions(applyOverridesToPermissions(base, everyone), roles);
}

/** Bits the server checks per channel in a text channel. */
const TEXT_CHANNEL_PERMISSIONS =
  PERMISSIONS.VIEW_CHANNEL |
  PERMISSIONS.SEND_MESSAGES |
  PERMISSIONS.ATTACH_FILES |
  PERMISSIONS.ADD_REACTIONS |
  PERMISSIONS.EMBED_LINKS |
  PERMISSIONS.READ_MESSAGE_HISTORY;

/** Bits the server checks per channel in a voice channel (it has no chat). */
const VOICE_CHANNEL_PERMISSIONS =
  PERMISSIONS.VIEW_CHANNEL |
  PERMISSIONS.CONNECT_VOICE |
  PERMISSIONS.SPEAK |
  PERMISSIONS.STREAM_VIDEO |
  PERMISSIONS.MUTE_MEMBERS |
  PERMISSIONS.DEAFEN_MEMBERS |
  PERMISSIONS.MOVE_MEMBERS |
  PERMISSIONS.KICK_VOICE;

/**
 * Which permissions a channel override can meaningfully set for this
 * channel: exactly the bits some server check resolves per channel for its
 * type. Anything else in an override would be a switch that changes
 * nothing, so the server rejects it and the settings screen never shows it.
 * Categories carry no overrides (they don't cascade to their channels).
 */
export function channelOverridePermissions(channel: {
  type: string;
  tournament?: boolean | null;
}): bigint {
  switch (channel.type) {
    case 'TEXT':
    case 'ANNOUNCE':
      return TEXT_CHANNEL_PERMISSIONS;
    case 'VOICE':
      // Going on air is only possible from a tournament room.
      return VOICE_CHANNEL_PERMISSIONS | (channel.tournament ? PERMISSIONS.BROADCAST_VOICE : 0n);
    default:
      return 0n;
  }
}

/**
 * Fold a member's guild-level permissions: the @everyone baseline OR'd with
 * every explicit role they hold.
 *
 * `@everyone` is a real role, not a constant. Both sides used to seed this fold
 * with {@link DEFAULT_MEMBER_PERMISSIONS} instead, which quietly made that role
 * un-restrictable: an owner could untick «Писать сообщения» on @everyone, the
 * row would save, and every member would keep writing because the constant was
 * OR'd back in on the next request. The roles screen showed the ticks it had
 * stored, so the UI disagreed with reality in both directions.
 *
 * `everyone` is nullable on purpose: a guild whose @everyone row is missing
 * (old data, a partial import) falls back to the shipped default rather than
 * locking every member out of a server nobody can fix without one.
 */
export function computeMemberPermissions(opts: {
  everyone: bigint | number | null | undefined;
  roles: Iterable<bigint | number>;
}): bigint {
  const base =
    opts.everyone === null || opts.everyone === undefined
      ? DEFAULT_MEMBER_PERMISSIONS
      : BigInt(opts.everyone);
  let perms = base;
  for (const role of opts.roles) perms |= BigInt(role);
  return perms;
}
